import { useState } from 'react';

import { useGetAdminPaymentMethodsQuery, useSetAdminPaymentMethodMutation } from '@entities/admin';
import type { PaymentMethodDto } from '@entities/deposit/api/depositApi';
import { apiErrorMessage } from '@shared/api/baseApi';
import { haptics } from '@shared/lib/telegram';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Spinner } from '@shared/ui/Spinner';
import styles from './shared.module.css';

function PaymentEditor({ method }: { method: PaymentMethodDto }) {
  const [enabled, setEnabled] = useState(method.enabled);
  const [name, setName] = useState(method.name);
  const [accountName, setAccountName] = useState(method.accountName);
  const [accountNumber, setAccountNumber] = useState(method.accountNumber);
  const [save, { isLoading: saving }] = useSetAdminPaymentMethodMutation();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const changed = () => { setSaved(false); setError(null); };
  const submit = async () => {
    setError(null); setSaved(false);
    try {
      const result = await save({ id: method.id, name, accountName, accountNumber, enabled }).unwrap();
      setEnabled(result.enabled); setName(result.name); setAccountName(result.accountName); setAccountNumber(result.accountNumber);
      setSaved(true); haptics.notify('success');
    } catch (cause) {
      setError(apiErrorMessage(cause, 'Could not save payment details.')); haptics.notify('error');
    }
  };

  return (
    <Card className={styles.row}>
      <div className={styles.rowHead}>
        <img className={styles.logo} src={method.logoUrl} alt="" />
        <p className={styles.rowTitle}>{method.name}</p>
        <span className={styles.badge}>{!method.enabled ? 'Disabled' : method.isPlaceholder ? 'Coming soon' : 'Ready'}</span>
      </div>
      <div className={styles.rowHead}>
        <span className={styles.rowMain} id={`${method.id}-enabled-label`}>Show to customers</span>
        <button type="button" role="switch" aria-checked={enabled}
          aria-labelledby={`${method.id}-enabled-label`}
          className={enabled ? styles.switchOn : styles.switchOff} disabled={saving}
          onClick={() => { setEnabled(!enabled); changed(); haptics.select(); }}>
          <span />
        </button>
      </div>
      <label className={styles.hint} htmlFor={`${method.id}-name`}>Payment method name</label>
      <input id={`${method.id}-name`} className={styles.input} value={name} maxLength={60}
        disabled={saving} onChange={(event) => { setName(event.target.value); changed(); }} />
      <label className={styles.hint} htmlFor={`${method.id}-holder`}>Account holder name</label>
      <input id={`${method.id}-holder`} className={styles.input} value={accountName} maxLength={120}
        placeholder="Enter account holder name" disabled={saving}
        onChange={(event) => { setAccountName(event.target.value); changed(); }} />
      <label className={styles.hint} htmlFor={`${method.id}-number`}>Account or phone number</label>
      <input id={`${method.id}-number`} className={styles.input} type="text" value={accountNumber}
        maxLength={80} placeholder="Enter account or phone number" disabled={saving}
        onChange={(event) => { setAccountNumber(event.target.value); changed(); }} />
      <div className={styles.actions}>
        <Button loading={saving} disabled={!name.trim()} onClick={() => void submit()}>Save details</Button>
        {saved ? <span className={styles.hint} role="status">Saved</span> : null}
      </div>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
    </Card>
  );
}

export function AdminPaymentMethods() {
  const { data, isLoading, error, refetch } = useGetAdminPaymentMethodsQuery();
  if (isLoading) return <Spinner />;
  if (error || !data) return (
    <div className={styles.section}>
      <p className={styles.error}>Could not load payment settings.</p>
      <Button variant="secondary" onClick={() => void refetch()}>Try again</Button>
    </div>
  );
  return (
    <div className={styles.section}>
      <p className={styles.hint}>Save to apply your changes. Disabled methods are hidden from customers. An enabled method with missing account details shows as coming soon.</p>
      {data.map((method) => <PaymentEditor key={method.id} method={method} />)}
    </div>
  );
}
